export default function PrivacyPolicy() {
  return (
    <main className="container py-24 max-w-4xl">
      <h1 className="text-4xl font-extrabold text-[var(--text)] mb-2">
        Privacy Policy for LoanPulse
      </h1>

      <p className="text-[var(--muted)] mb-10">
        <strong>Last updated:</strong> December 2025
      </p>

      <section className="space-y-10 text-[var(--text)] leading-relaxed">

        <p>
          LoanPulse ("we", "our", "us") respects your privacy and is committed to
          protecting the personal information of users ("you", "your"). This
          Privacy Policy explains how information is collected, used, disclosed,
          and safeguarded when you use the <strong>LoanPulse mobile application</strong> and
          related services (collectively, the "App").
        </p>

        <p>
          By downloading, accessing, or using LoanPulse, you agree to the terms
          of this Privacy Policy.
        </p>

        {/* 1 */}
        <div>
          <h2 className="text-2xl font-semibold mb-3">1. About LoanPulse</h2>
          <p>
            LoanPulse is a productivity and checklist-sharing application designed
            for professionals such as loan agents, DSAs, sales executives, and
            consultants to create and share loan-related checklists and information
            with customers.
          </p>
          <p className="mt-3">
            LoanPulse <strong>does not provide loans</strong>, <strong>does not approve or
            reject credit</strong>, and <strong>does not act as a lender, broker, or
            financial institution</strong>.
          </p>
        </div>

        {/* 2 */}
        <div>
          <h2 className="text-2xl font-semibold mb-3">2. Information We Collect</h2>

          <h3 className="text-lg font-semibold mt-4 mb-2">
            2.1 Information You Provide
          </h3>
          <ul className="list-disc pl-6 space-y-2">
            <li>Name (agent or user name)</li>
            <li>Mobile number</li>
            <li>Email address (if provided)</li>
            <li>Customer details entered by you (such as customer name and contact number)</li>
            <li>Checklist or message content created within the App</li>
          </ul>

          <h3 className="text-lg font-semibold mt-6 mb-2">
            2.2 Automatically Collected Information
          </h3>
          <ul className="list-disc pl-6 space-y-2">
            <li>Device information (device model, operating system version)</li>
            <li>App usage data (features used, app performance)</li>
            <li>Log data (IP address, date and time of access)</li>
          </ul>

          <p className="mt-3">
            We may use analytics tools to understand usage patterns and improve
            App performance and user experience.
          </p>

          <h3 className="text-lg font-semibold mt-6 mb-2">
            2.3 Permissions
          </h3>
          <ul className="list-disc pl-6 space-y-2">
            <li>Internet (to sync and share data)</li>
            <li>Storage (to save generated files or PDFs)</li>
            <li>Camera (optional, for document scanning if enabled)</li>
          </ul>

          <p className="mt-3">
            Permissions are requested only when required and are used solely to
            provide core App functionality.
          </p>
        </div>

        {/* 3 */}
        <div>
          <h2 className="text-2xl font-semibold mb-3">3. How We Use Information</h2>
          <ul className="list-disc pl-6 space-y-2">
            <li>Provide and improve App functionality</li>
            <li>Enable checklist creation and sharing</li>
            <li>Maintain user accounts and preferences</li>
            <li>Improve performance, reliability, and user experience</li>
            <li>Communicate important updates or service information</li>
          </ul>
          <p className="mt-3">
            We <strong>do not</strong> use your data for automated credit
            decision-making or profiling.
          </p>
        </div>

        {/* 4 */}
        <div>
          <h2 className="text-2xl font-semibold mb-3">
            4. Data Sharing and Disclosure
          </h2>
          <p>We do <strong>not sell, rent, or trade</strong> your personal information.</p>
          <ul className="list-disc pl-6 space-y-2 mt-3">
            <li>Service providers (cloud hosting, analytics) strictly for App operation</li>
            <li>Legal or regulatory requirements</li>
            <li>Protection of rights, safety, or security</li>
          </ul>
        </div>

        {/* 5 */}
        <div>
          <h2 className="text-2xl font-semibold mb-3">5. Data Storage and Security</h2>
          <ul className="list-disc pl-6 space-y-2">
            <li>Secure servers and encrypted communication</li>
            <li>Access controls and authentication mechanisms</li>
            <li>Data minimization and role-based access</li>
          </ul>
          <p className="mt-3">
            No method of transmission or storage is 100% secure, and absolute
            security cannot be guaranteed.
          </p>
        </div>

        {/* 6 */}
        <div>
          <h2 className="text-2xl font-semibold mb-3">6. Data Retention</h2>
          <p>
            Personal data is retained only as long as necessary to provide App
            services or comply with legal requirements.
          </p>
          <p className="mt-3">
            Data deletion requests will be processed within a reasonable time
            frame, subject to applicable laws.
          </p>
        </div>

        {/* 7 */}
        <div>
          <h2 className="text-2xl font-semibold mb-3">7. Your Rights</h2>
          <ul className="list-disc pl-6 space-y-2">
            <li>Access your personal data</li>
            <li>Request correction of inaccurate data</li>
            <li>Request deletion of your data</li>
            <li>Withdraw consent where applicable</li>
          </ul>
        </div>

        {/* 8 */}
        <div>
          <h2 className="text-2xl font-semibold mb-3">8. Children’s Privacy</h2>
          <p>
            LoanPulse is intended for users <strong>18 years and above</strong>.
            We do not knowingly collect data from children.
          </p>
        </div>

        {/* 9 */}
        <div>
          <h2 className="text-2xl font-semibold mb-3">9. Third-Party Links</h2>
          <p>
            The App may contain links to third-party platforms (such as WhatsApp).
            We are not responsible for their privacy practices.
          </p>
        </div>

        {/* 10 */}
        <div>
          <h2 className="text-2xl font-semibold mb-3">
            10. Changes to This Privacy Policy
          </h2>
          <p>
            Updates will be posted within the App or on our website. Continued
            use of the App constitutes acceptance of the updated policy.
          </p>
        </div>

        {/* 11 */}
        <div>
          <h2 className="text-2xl font-semibold mb-3">
            11. Contact Us & Grievance Redressal
          </h2>
          <p>
            For questions, concerns, or grievances related to this Privacy Policy,
            contact us at:
          </p>
          <p className="mt-3 font-medium">
            Email: <a href="mailto:loanpulse.app@gmail.com" className="text-[var(--primary)]">loanpulse.app@gmail.com</a>
          </p>
          <p className="font-medium">
            Website: <a href="https://loanpulse.in" className="text-[var(--primary)]">https://loanpulse.in</a>
          </p>
        </div>

        <p className="text-sm text-[var(--muted)] pt-6">
          This Privacy Policy is designed to comply with the Digital Personal Data
          Protection Act, 2023, and Google Play Developer Policy requirements.
        </p>

      </section>
    </main>
  );
}
